import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import HeroStyle4 from "../Hero/HeroStyle4";
import Spacing from "../Spacing";
import SectionHeading from "../SectionHeading";
import Brands from "../Brands";
import FunFact from "../FunFact";
import AboutStyle4 from "../About/AboutStyle4";
import AboutStyle5 from "../About/AboutStyle5";
import IconBoxStyle2 from "../IconBox/IconBoxStyle2";
import PortfolioSlider from "../Slider/PortfolioSlider";
import PricingTableList from "../PricingTable/PricingTableList";
import { pageTitle } from "../../helpers/PageTitle";
import Marquee from "../Marquee";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";

// Animación para el Hero
const AnimatedHero = ({ children }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Animación para la sección de estadísticas (FunFact)
const AnimatedStats = ({ children }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -100 }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Animación para las secciones About
const AnimatedAbout = ({ children }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 100 }}
      animate={inView ? { 
        opacity: 1, 
        y: 0,
      } : { opacity: 0, y: 100 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Animación para la sección de servicios
const AnimatedServices = ({ children }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95, y: 50 }}
      animate={inView ? { 
        opacity: 1, 
        scale: 1,
        y: 0
      } : { opacity: 0, scale: 0.95, y: 50 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Animación para el portafolio
const AnimatedPortfolio = ({ children }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: 100 }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 100 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Animación para la sección de precios
const AnimatedPricing = ({ children }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50, scale: 0.98 }}
      animate={inView ? { 
        opacity: 1, 
        y: 0,
        scale: 1
      } : { opacity: 0, y: 50, scale: 0.98 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

export default function DigitalAgencyPage({ darkMode }) {
  pageTitle("Digital Agency");
  const { t } = useTranslation();

  // Usar los datos de FunFact desde las traducciones
  const funfactData = t('funfact.items', { returnObjects: true });
  
  const brandList = [
    {
      logoSrc: "/images/unalom/Hoshin_1.png",
      logoAlt: "Hoshin",
      href: "https://hoshincorp.com"
    },
    {
      logoSrc: "/images/unalom/Personnal_Staff_1.png",
      logoAlt: "Personal Staff",
      href: "https://personnelstaff.com/clients"
    },
    {
      logoSrc: "/images/unalom/Unique_1.png",
      logoAlt: "Unique",
      href: "https://uniqueenter.com"
    },
    {
      logoSrc: "/images/unalom/novisto_1.png",
      logoAlt: "Novisto",
      href: "https://novisto.com"
    },
  ];
  
  const brandListDark = [...brandList];

  // Usar los datos de Portfolio desde las traducciones
  const portfolioData = t('portfolio.items', { returnObjects: true }).map((item, index) => ({
    ...item,
    // Procesar los saltos de línea en el título para mantener el formato HTML
    title: item.title.includes(',') ? 
      item.title.split(',')[0] + ',<br />' + item.title.split(',')[1] : 
      item.title.replace(' ', '<br />'),
    // Asignar las imágenes correctamente según el índice o tipo
    thumbnailSrc: getPortfolioImageByIndex(index)
  }));

  return (
    <>
      <AnimatedHero>
        <HeroStyle4
          thumbnailSrc={
            darkMode
              ? "/images/digital-agency/hero_bg_dark.jpeg"
              : "/images/digital-agency/hero_bg.jpeg"
          }
          videoSrc="/videos/VID-20250408-WA0023.mp4"
          bgUrl="/images/unalom/ScreenshotUnalome.png"
        />
      </AnimatedHero>

      <AnimatedStats>
        <div className="cs_primary_bg">
          <Spacing lg="125" md="80" />
          <div className="container">
            <FunFact data={funfactData} colorVariant="cs_color_1" />
          </div>
          <Spacing lg="125" md="80" />
        </div>
      </AnimatedStats>

      <AnimatedAbout>
        <Spacing lg="150" md="80" />
        <AboutStyle4
          thumbnailSrc="videos/LogoRevealIgnicin_free.mp4"
          miniTitle={t("about.page.info.miniTitle")}
          title={t("about.page.info.title")}
          subTitle={t("about.page.info.description")}
          btnText={t("about.buttonText")}
          btnUrl="/service"
          darkMode={darkMode}
        />
      </AnimatedAbout>

      <AnimatedAbout>
        <Spacing lg="150" md="80" />
        <AboutStyle5
          thumbnailSrc="images/unalom/conversionesNombreEvento.jpeg"
          miniTitle={t("results.miniTitle")}
          title={t("results.title")}
          subTitle={t("results.description")}
          progressBarList={[
            { title: t("results.purchase"), percentage: "65" },
            { title: t("results.totalRevenue"), percentage: "42" },
          ]}
        />
      </AnimatedAbout>

      <AnimatedServices>
        <Spacing lg="100" md="80" />
        <section className='cs_primary_bg '>
          <Spacing lg="143" md="75" />
          <div className="container">
            <SectionHeading
              title={t("serviceSection.title")}
              subTitle={t("serviceSection.subTitle")}
              variantColor="cs_white_color"
            />
            <Spacing lg="85" md="45" />
            <div className="row cs_gap_y_45">
              <div className="col-lg-3 col-sm-6">
                <Link to="/portfolio">
                  <IconBoxStyle2
                    iconSrc="/images/digital-agency/service_icon_1.svg"
                    title={t("services.webDev.title")}
                    subTitle={t("services.webDev.description")}
                    features={t("services.webDev.features", { returnObjects: true })}
                  />
                </Link>
              </div>
              <div className="col-lg-3 col-sm-6">
                <Link to="/portfolio/portfolio-details">
                  <IconBoxStyle2
                    iconSrc="/images/digital-agency/service_icon_2.svg"
                    title={t("services.marketing.title")}
                    subTitle={t("services.marketing.description")}
                    features={t("services.marketing.features", { returnObjects: true })}
                  />
                </Link>
              </div>
              <div className="col-lg-3 col-sm-6">
                <Link to="/case-study-details">
                  <IconBoxStyle2
                    iconSrc="/images/digital-agency/service_icon_3.svg"
                    title={t("services.merchandise.title")}
                    subTitle={t("services.merchandise.description")}
                    features={t("services.merchandise.features", { returnObjects: true })}
                  />
                </Link>
              </div>
              <div className="col-lg-3 col-sm-6">
                <Link to="/blog">
                  <IconBoxStyle2
                    iconSrc="/images/digital-agency/service_icon_4.svg"
                    title={t("services.events.title")}
                    subTitle={t("services.events.description")}
                    features={t("services.events.features", { returnObjects: true })}
                  />
                </Link>
              </div>
              <a
                href="https://calendly.com/nicolas-rs/new-meeting"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="text-center">
                  <Spacing lg="80" md="50" />
                  <span className="cs_btn cs_style_1_2 ">
                    {t("serviceSection.button")}
                    <span>
                      <i>
                        <Icon icon="fa6-solid:arrow-right" />
                      </i>
                    </span>
                  </span>
                </div>
              </a>
            </div>
          </div>
          <Spacing lg="143" md="75" />
        </section>
      </AnimatedServices>

      <AnimatedPortfolio>
        <section>
          <Spacing lg="143" md="75" />
          <div className="container">
            <SectionHeading
              title={t("portfolio.sectionTitle")}
              subTitle={t("portfolio.sectionSubTitle")}
            />
            <Spacing lg="85" md="45" />
          </div>
          <PortfolioSlider data={portfolioData} />
          <Spacing lg="150" md="80" />
        </section>
      </AnimatedPortfolio>

      <AnimatedPricing>
        <section className="cs_gray_bg_2 cs_shape_animation_2">
          <div className="cs_shape_1 position-absolute">
            <svg
              width={57}
              height={41}
              viewBox="0 0 57 41"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.5"
                d="M55.1923 32.7347C52.209 37.0618 42.704 43.3879 40.3199 34.8097C39.0585 30.2359 42.6536 24.5172 43.1266 19.9687C43.8204 13.2821 39.973 5.41874 32.4359 6.05135C31.8886 6.10385 31.3452 6.19047 30.8086 6.31072C29.5308 4.21019 27.6918 2.5107 25.5002 1.40489C23.3085 0.299087 20.8519 -0.168816 18.4087 0.0542238C11.0797 0.743766 5.31489 7.39247 2.40097 13.6616C0.297443 17.9159 -0.0470809 22.831 1.44227 27.3386C2.02253 28.9897 4.52649 28.3381 4.07868 26.6111C2.33789 19.994 6.55742 12.4976 11.1238 7.99978C15.6903 3.50194 23.1517 2.572 26.8919 7.71511C22.3191 9.96086 18.2321 14.1614 15.6019 17.957C12.4483 22.5245 9.15598 31.3746 15.1794 35.2589C21.0135 39.0165 27.7181 36.5241 30.9852 30.8306C34.3217 24.9727 34.2461 16.9828 32.6377 10.6251C32.4379 9.84013 32.1764 9.07227 31.8556 8.32873C34.7948 7.65817 37.6078 8.80319 39.4747 11.7765C42.4517 16.502 40.9317 21.6009 39.5126 26.5289C38.1691 31.1785 36.6617 38.618 42.9185 40.5728C48.4057 42.2935 54.6246 38.6054 56.4853 33.2978C56.7691 32.507 55.6275 32.1085 55.1923 32.7347ZM27.8253 28.0345C26.6396 30.7231 24.5519 32.7537 21.5181 32.9751C16.3336 33.3547 15.3434 29.0909 16.4723 25.0739C18.1122 19.3298 23.2274 13.4845 28.1407 10.0684C28.1664 10.1199 28.1896 10.1727 28.2101 10.2266C30.2749 16.0071 30.1378 22.349 27.8253 28.0345Z"
                fill="#4F4747"
              />
            </svg>
          </div>
          <div className="cs_shape_2 position-absolute">
            <svg
              width={21}
              height={24}
              viewBox="0 0 21 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.5"
                d="M1.83883 0.992714L20.9086 13.6609L0.402728 23.8417L1.83883 0.992714Z"
                fill="#4F4747"
              />
            </svg>
          </div>
          <div className="cs_height_143 cs_height_lg_75" />
          <div className="container">
            <SectionHeading
              title={t("pricing.title")}
              subTitle={t("pricing.subTitle")}
            />
            <Spacing lg="85" md="45" />
          </div>
          
          <div className="container">
            <PricingTableList />
          </div>
          <div className="cs_height_150 cs_height_lg_80" />
        </section>
      </AnimatedPricing>

      <AnimatedStats>
        <Spacing lg="135" md="70" />
        <Marquee text={t("marquee.text")} />
        <Spacing lg="135" md="70" />
        <div className="cs_primary_bg">
          <Spacing lg="125" md="80" />
          <div className="container">
            <Brands data={darkMode ? brandListDark : brandList} />
          </div>
          <Spacing lg="125" md="80" />
        </div>
      </AnimatedStats>
    </>
  );
}

// Función auxiliar para asignar imágenes según el índice o categoría
function getPortfolioImageByIndex(index) {
  const images = [
    "/images/unalom/mas-crecimiento-menos-gastos.jpg",
    "/images/unalom/paginas-venden-por-ti.jpg", 
    "/images/unalom/branding.jpeg",
    "/images/unalom/eventos-activacion-marca.jpg",
    "/images/unalom/staff-especializado.jpg"
  ];
  
  // Si el índice está fuera de rango, devolvemos la primera imagen como respaldo
  return index < images.length ? images[index] : images[0];
}
